import { useCallback, useState } from "react";
import { format } from "@react-input/mask";
import * as yup from "yup";
import { createGalaxPay, formatDateToCelCoin } from "@/utils";
import { Notyf } from "notyf";
import Button from "@/components/button";
import Field from "@/components/field";
import { useAppSelector } from "@/redux/hooks";
import fallbackLogo from "@/assets/logo.png";

export interface PaymentData {
  holder: string;
  cardNumber: string;
  valid: string;
  cvv: string;
}

interface PaymentProps {
  paymentData: PaymentData | null;
  loading: boolean;
  submitData: (dataPayment: PaymentData, hashCard: string) => void;
  backStep: () => void;
}

const paymentSchema = yup.object().shape({
  holder: yup
    .string()
    .required("Informe o nome escrito no cartão")
    .max(30, "Nome deve ter no máximo 30 caracteres"),
  cardNumber: yup
    .string()
    .required("Informe o número do cartão")
    .min(19, "Número inválido")
    .max(30, "Número deve ter no máximo 30 caracteres"),
  valid: yup
    .string()
    .required("Digite a data de validade")
    .max(7, "Data deve ter no máximo 7 caracteres")
    .test("validate-valid", "Data inválida", (value) => {
      if (value) {
        const dates = value.split("/");
        const today = new Date();
        const ed = new Date();
        const year = 2000 + parseInt(dates[1]);
        const month = parseInt(dates[0]);
        ed.setFullYear(year, month, 0);
        if (ed < today) {
          return false;
        }
      }
      return true;
    }),
  cvv: yup
    .string()
    .required("Digite a senha")
    .min(3, "Senha inválida")
    .max(4, "Senha deve ter no máximo 4 caracteres"),
});

export default function Payment(props: PaymentProps) {
  const tenant = useAppSelector((state) => state.tenant.loginTenant);
  const tokenizePublicToken = useAppSelector(
    (state) => state.tenant.tokenizePublicToken
  );
  const tokenizeIsProduction = useAppSelector(
    (state) => state.tenant.tokenizeIsProduction
  );
  const logo = tenant?.LogoUrl || fallbackLogo;
  const [holder, setHolder] = useState(props?.paymentData?.holder || "");
  const [cardNumber, setCardNumber] = useState(
    props?.paymentData?.cardNumber || ""
  );
  const [valid, setValid] = useState(props?.paymentData?.valid || "");
  const [cvv, setCvv] = useState(props?.paymentData?.cvv || "");
  const [errors, setErrors] = useState<Record<string, string[]> | null>(null);
  const instanceGalaxPay =
    tokenizePublicToken
      ? createGalaxPay(tokenizePublicToken, tokenizeIsProduction)
      : null;

  const toggleBackCard = () => {
    const cardEl = document.getElementById("creditCard");
    if (cardEl?.classList.contains("seeBack")) {
      cardEl.classList.remove("seeBack");
    } else {
      cardEl?.classList.add("seeBack");
    }
  };
  const showBackCard = () => {
    const cardEl = document.getElementById("creditCard");
    if (cardEl?.classList.contains("seeBack")) {
      cardEl.classList.remove("seeBack");
    }
  };
  const hideBackCard = () => {
    const cardEl = document.getElementById("creditCard");
    if (!cardEl?.classList.contains("seeBack")) {
      cardEl?.classList.add("seeBack");
    }
  };

  const validateFormPayment = async () => {
    const data: PaymentData = {
      holder,
      cardNumber,
      valid,
      cvv,
    };

    try {
      await paymentSchema.validate(data, { abortEarly: false });
      return true;
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const nextErrors: Record<string, string[]> = {};
        err.inner.forEach((element) => {
          const path = element.path || "root";
          if (nextErrors[path]) {
            nextErrors[path].push(element?.message || "");
          } else {
            nextErrors[path] = [element?.message || ""];
          }
        });
        setErrors(nextErrors);
        return false;
      }
    }
  };

  const isError = useCallback(
    (field: string) => (errors && errors[field] && errors[field][0]) || null,
    [errors]
  );

  const validateField = async (
    schema: yup.Schema,
    fieldName: string,
    value: string,
    currentErrors: Record<string, string[]> | null,
    setCurrentErrors: (errors: Record<string, string[]> | null) => void
  ) => {
    try {
      await schema.validateAt(fieldName, { [fieldName]: value });
      const allErrors = { ...currentErrors };
      delete allErrors[fieldName];
      setCurrentErrors(allErrors);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        const allErrors = { ...currentErrors };
        allErrors[fieldName] = err.errors;
        setCurrentErrors(allErrors);
        return false;
      }
    }
  };

  const submitData = async () => {
    const data: PaymentData = {
      holder,
      cardNumber,
      valid,
      cvv,
    };

    if (await validateFormPayment()) {
      if (!instanceGalaxPay) {
        const notyf = new Notyf({
          duration: 3000,
          position: { x: "center", y: "bottom" },
        });
        notyf.error(
          "Pagamento indisponível: a conta Celcoin deste estabelecimento não está configurada."
        );
        return;
      }

      try {
        const card = instanceGalaxPay.newCard({
          number: data.cardNumber,
          holder: data.holder,
          expiresAt: formatDateToCelCoin(data.valid),
          cvv: data.cvv,
        });

        instanceGalaxPay.hashCreditCard(
          card,
          (hash) => {
            props?.submitData(data, hash);
          },
          () => {
            const notyf = new Notyf({
              duration: 3000,
              position: { x: "center", y: "bottom" },
            });
            notyf.error("Não foi possível gerar o hash do cartão");
          }
        );
      } catch (e) {
        const nextErrors: Record<string, string[]> = {};
        if ((e as Error)?.message === "Número do cartão inválido.") {
          nextErrors["cardNumber"] = ["Número do cartão inválido."];
          setErrors(nextErrors);
        }
      }
    }
  };

  return (
    <div className="flex flex-col gap-6 pb-10">
      <h1 className="font-display text-3xl font-semibold md:text-4xl">
        Dados do cartão
      </h1>

      <button
        type="button"
        className="mx-auto w-full max-w-sm"
        onClick={toggleBackCard}
        aria-label="Virar cartão"
      >
        <div className="h-52 w-full" style={{ perspective: 1000 }}>
          <div
            id="creditCard"
            className="crediCard relative h-52 w-full cursor-pointer transition-transform duration-500 ease-soft seeBack"
            style={{ transformStyle: "preserve-3d" }}
          >
            <div
              className="absolute inset-0 overflow-hidden rounded-2xl p-6 text-left text-white shadow-2xl"
              style={{
                backfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                background: `linear-gradient(135deg, var(--tenant-accent-color), var(--tenant-accent-color-darkest))`,
              }}
            >
              <div className="flex justify-end">
                <img src={logo} alt="" className="h-10 w-10 object-contain" />
              </div>
              <p className="mt-6 text-xs text-white/70">Número do cartão</p>
              <p className="mt-1 h-6 font-medium tracking-[0.18em]">
                {cardNumber}
              </p>
              <div className="mt-6 flex justify-between">
                <div>
                  <p className="text-xs text-white/70">Nome</p>
                  <p className="h-6 font-medium tracking-wide">{holder}</p>
                </div>
                <div>
                  <p className="text-xs text-white/70">Validade</p>
                  <p className="h-6 w-14 font-medium">{valid}</p>
                </div>
              </div>
            </div>
            <div
              className="absolute inset-0 overflow-hidden rounded-2xl text-white shadow-2xl"
              style={{
                backfaceVisibility: "hidden",
                background: `linear-gradient(135deg, var(--tenant-accent-color-darker), var(--tenant-accent-color-darkest))`,
              }}
            >
              <div className="mt-8 h-10 bg-black" />
              <div className="mt-5 px-6">
                <div className="flex">
                  <div className="h-8 flex-1 bg-white/80" />
                  <p className="flex w-14 items-center bg-white pl-3 text-black">
                    {cvv}
                  </p>
                </div>
                <p className="mt-2 text-right text-xs text-white/70">CVV</p>
                <div className="mt-3 flex justify-end">
                  <img src={logo} alt="" className="h-10 w-10 object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </button>

      <Field
        label="Número do cartão"
        placeholder="xxxx xxxx xxxx xxxx"
        value={cardNumber}
        onClick={hideBackCard}
        onChange={(vl) => {
          setCardNumber(
            format(vl.target.value, {
              mask: "____ ____ ____ ____",
              replacement: { _: /\d/ },
            })
          );
        }}
        error={isError("cardNumber")}
        onBlur={() =>
          validateField(
            paymentSchema,
            "cardNumber",
            cardNumber,
            errors,
            setErrors
          )
        }
      />
      <Field
        label="Nome no cartão"
        value={holder}
        onClick={hideBackCard}
        onChange={(vl) => setHolder(vl.target.value)}
        error={isError("holder")}
        onBlur={() =>
          validateField(paymentSchema, "holder", holder, errors, setErrors)
        }
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Validade"
          placeholder="xx/xx"
          value={valid}
          onClick={hideBackCard}
          onChange={(vl) => {
            setValid(
              format(vl.target.value, {
                mask: "__/__",
                replacement: { _: /\d/ },
              })
            );
          }}
          error={isError("valid")}
          onBlur={() =>
            validateField(paymentSchema, "valid", valid, errors, setErrors)
          }
        />
        <Field
          label="CVV"
          placeholder="xxx"
          maxLength={4}
          value={cvv}
          onClick={showBackCard}
          onChange={(vl) => {
            setCvv(
              format(vl.target.value, {
                mask: "____",
                replacement: { _: /\d/ },
              })
            );
          }}
          error={isError("cvv")}
          onBlur={() =>
            validateField(paymentSchema, "cvv", cvv, errors, setErrors)
          }
        />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button
          variant="secondary"
          onClick={() => {
            hideBackCard();
            props?.backStep();
          }}
        >
          Voltar
        </Button>
        <Button
          loading={props.loading}
          onClick={() => {
            hideBackCard();
            submitData();
          }}
        >
          Finalizar pagamento
        </Button>
      </div>
    </div>
  );
}
